import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ShieldAlert, X, ArrowRight, Filter } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost, apiPut } from '../lib/api';
import { toast } from 'sonner';

const PROB = [{ v: 'L', l: 'Low' }, { v: 'M', l: 'Medium' }, { v: 'H', l: 'High' }];
const IMPACT = PROB;
const STATUSES = ['Open', 'Mitigating', 'Accepted', 'Closed'];
const PMAP = { L: 1, M: 2, H: 3 };

const cellColor = (score) => {
  if (score >= 6) return 'bg-red-500';
  if (score >= 3) return 'bg-amber-500';
  return 'bg-emerald-500';
};
const scoreOf = (p, i) => (PMAP[p] || 2) * (PMAP[i] || 2);

const inputCls =
  'w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none';

export default function Risks() {
  const [risks, setRisks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('Open');

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([apiGet('/risks'), apiGet('/projects')]);
      setRisks(r);
      setProjects(p);
    } catch {
      toast.error('Failed to load risks');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const projectName = (id) => projects.find(p => p.id === id)?.code || '—';

  const filtered = useMemo(() => {
    return risks.filter(r =>
      (!filterProject || r.project_id === filterProject) &&
      (filterStatus === 'All' || (r.status || 'Open') === filterStatus)
    );
  }, [risks, filterProject, filterStatus]);

  const matrixCounts = useMemo(() => {
    const m = {};
    for (const p of ['H', 'M', 'L']) {
      for (const i of ['L', 'M', 'H']) {
        m[`${p}-${i}`] = filtered.filter(r => r.probability === p && r.impact === i).length;
      }
    }
    return m;
  }, [filtered]);

  const byProject = useMemo(() => {
    const m = {};
    for (const r of filtered) {
      const pid = r.project_id || 'unassigned';
      const b = m[pid] || { project_id: pid, total: 0, high: 0, medium: 0, low: 0 };
      b.total++;
      if ((r.score || 0) >= 6) b.high++;
      else if ((r.score || 0) >= 3) b.medium++;
      else b.low++;
      m[pid] = b;
    }
    return Object.values(m).sort((a, b) => b.high - a.high || b.total - a.total);
  }, [filtered]);

  const stats = useMemo(() => ({
    total: filtered.length,
    high: filtered.filter(r => (r.score || 0) >= 6).length,
    open: filtered.filter(r => (r.status || 'Open') === 'Open').length,
    mitigating: filtered.filter(r => r.status === 'Mitigating').length,
  }), [filtered]);

  return (
    <div>
      <PageHeader
        overline="Project Management"
        title="Portfolio Risk Register"
        subtitle={`${risks.length} risks across ${projects.length} projects · enterprise-wide visibility & mitigation tracking`}
        actions={
          <button data-testid="risk-portfolio-add-btn" onClick={() => setAdding(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2c3658] hover:-translate-y-px transition-all">
            <Plus size={15} /> New Risk
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Total Risks" value={stats.total} />
        <Stat label="High Severity (≥6)" value={stats.high} accent="text-red-600" />
        <Stat label="Open" value={stats.open} accent="text-amber-700" />
        <Stat label="Mitigation in Flight" value={stats.mitigating} accent="text-blue-700" />
      </div>

      {/* Filter strip */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex flex-wrap items-center gap-3">
        <Filter size={14} className="text-slate-400" />
        <span className="kpi-overline">Filter</span>
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)} data-testid="risk-filter-project"
          className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm bg-white">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="risk-filter-status"
          className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm bg-white">
          <option value="All">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Heatmap */}
        <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="risk-heatmap">
          <div className="kpi-overline mb-3">3×3 Severity Heatmap</div>
          <div className="flex">
            <div className="w-10" />
            <div className="flex-1 grid grid-cols-3 gap-1 text-center text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">
              <div>Low</div><div>Med</div><div>High</div>
            </div>
          </div>
          <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 text-center mb-2">Impact →</div>
          {['H', 'M', 'L'].map(p => (
            <div key={p} className="flex items-center mb-1">
              <div className="w-10 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 text-right pr-2">{({ H: 'High', M: 'Med', L: 'Low' })[p]}</div>
              <div className="flex-1 grid grid-cols-3 gap-1">
                {['L', 'M', 'H'].map(i => {
                  const score = scoreOf(p, i);
                  const count = matrixCounts[`${p}-${i}`];
                  return (
                    <div key={i}
                      data-testid={`risk-heatmap-cell-${p}-${i}`}
                      className={`aspect-square rounded-sm ${cellColor(score)} text-white flex flex-col items-center justify-center font-bold ${count ? 'opacity-100' : 'opacity-30'}`}>
                      <span className="text-3xl font-display tabular-nums">{count}</span>
                      <span className="text-[9px] font-mono opacity-80">score {score}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 text-center mt-1">↑ Probability</div>
        </div>

        {/* By project */}
        <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="risk-by-project">
          <div className="kpi-overline mb-3">Risks by Project</div>
          {byProject.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No risks match current filter</div>
          ) : (
            <div className="space-y-2">
              {byProject.map(b => (
                <Link key={b.project_id} to={b.project_id !== 'unassigned' ? `/app/projects/${b.project_id}` : '#'}
                  className="flex items-center justify-between gap-3 p-2.5 border border-slate-200 hover:border-[#3b4872]/50 rounded-sm group">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm">{projectName(b.project_id)}</div>
                    <div className="text-xs text-slate-500">{projects.find(p => p.id === b.project_id)?.name || 'Portfolio-wide'}</div>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] font-display font-bold tabular-nums">
                    {b.high > 0 && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded-sm">{b.high} H</span>}
                    {b.medium > 0 && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-sm">{b.medium} M</span>}
                    {b.low > 0 && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-sm">{b.low} L</span>}
                    <span className="ml-1 text-slate-500">· {b.total} total</span>
                    <ArrowRight size={12} className="ml-1 group-hover:translate-x-1 group-hover:text-[#3b4872] transition-all" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Risk list */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="kpi-overline">Risk Register · {filtered.length} {filtered.length === 1 ? 'item' : 'items'}</div>
        </div>
        {loading ? (
          <div className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No risks found</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.sort((a, b) => (b.score || 0) - (a.score || 0)).map(r => (
              <RiskRow key={r.id} risk={r} projectName={projectName(r.project_id)} reload={load} />
            ))}
          </div>
        )}
      </div>

      {adding && <RiskFormModal projects={projects} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="kpi-overline mb-1">{label}</div>
      <div className={`font-display font-extrabold text-3xl tabular-nums ${accent || 'text-[#0F172A]'}`}>{value}</div>
    </div>
  );
}

function RiskRow({ risk, projectName, reload }) {
  const [busy, setBusy] = useState(false);
  const update = async (patch) => {
    setBusy(true);
    try {
      await apiPut(`/risks/${risk.id}`, { ...risk, ...patch });
      toast.success('Updated');
      reload();
    } catch { toast.error('Update failed'); }
    setBusy(false);
  };

  const color = (risk.score || 0) >= 6 ? 'border-l-red-500' : (risk.score || 0) >= 3 ? 'border-l-amber-500' : 'border-l-emerald-500';

  return (
    <div data-testid={`risk-row-${risk.id}`} className={`px-5 py-3 border-l-2 ${color} hover:bg-slate-50/40 flex items-start gap-4`}>
      <div className="w-12 text-center shrink-0">
        <div className="font-display font-extrabold text-2xl tabular-nums">{risk.score || '—'}</div>
        <div className="text-[9px] font-mono uppercase text-slate-400">P{risk.probability}/I{risk.impact}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm leading-snug font-medium">{risk.description || '(no description)'}</div>
        <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
          <Link to={risk.project_id ? `/app/projects/${risk.project_id}` : '#'} className="text-[#3b4872] font-display font-bold hover:underline">{projectName}</Link>
          {risk.owner && <span>Owner · <b className="text-slate-700">{risk.owner}</b></span>}
          {risk.mitigation && <span className="italic truncate max-w-md">↳ {risk.mitigation}</span>}
        </div>
      </div>
      <select value={risk.status || 'Open'} onChange={e => update({ status: e.target.value })} disabled={busy}
        data-testid={`risk-status-${risk.id}`}
        className="px-2 py-1 border border-slate-300 rounded-sm text-xs bg-white shrink-0">
        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
  );
}

function RiskFormModal({ projects, onClose, onSaved }) {
  const [form, setForm] = useState({ project_id: '', probability: 'M', impact: 'M', status: 'Open' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_id) { toast.error('Pick a project'); return; }
    if (!form.description) { toast.error('Description required'); return; }
    setBusy(true);
    try {
      const score = scoreOf(form.probability, form.impact);
      await apiPost('/risks', { ...form, score });
      toast.success('Risk added');
      onSaved();
    } catch { toast.error('Failed'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">New</div>
            <h3 className="font-display font-bold text-xl mt-1">Risk Entry</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <Field label="Project" full>
            <select value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })}
              data-testid="risk-form-project" className={`${inputCls} bg-white`}>
              <option value="">— Pick a project —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}
            </select>
          </Field>
          <Field label="Description" full>
            <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })}
              rows={2} data-testid="risk-form-description" className={inputCls}
              placeholder="e.g. Steel price escalation may breach budget..." />
          </Field>
          <Field label="Probability">
            <select value={form.probability} onChange={e => setForm({ ...form, probability: e.target.value })}
              data-testid="risk-form-probability" className={`${inputCls} bg-white`}>
              {PROB.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </Field>
          <Field label="Impact">
            <select value={form.impact} onChange={e => setForm({ ...form, impact: e.target.value })}
              data-testid="risk-form-impact" className={`${inputCls} bg-white`}>
              {IMPACT.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </Field>
          <Field label="Owner">
            <input value={form.owner || ''} onChange={e => setForm({ ...form, owner: e.target.value })}
              data-testid="risk-form-owner" className={inputCls} />
          </Field>
          <Field label="Category">
            <select value={form.category || ''} onChange={e => setForm({ ...form, category: e.target.value })}
              data-testid="risk-form-category" className={`${inputCls} bg-white`}>
              <option value="">—</option>
              {['Cost','Schedule','Quality','Safety','Resource','Contractual','External'].map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Mitigation Plan" full>
            <textarea value={form.mitigation || ''} onChange={e => setForm({ ...form, mitigation: e.target.value })}
              rows={2} data-testid="risk-form-mitigation" className={inputCls}
              placeholder="e.g. Lock-in steel rate via 6-month forward contract..." />
          </Field>
          <div className="col-span-2 bg-slate-50 border border-slate-200 rounded-sm p-3 flex items-center justify-between">
            <span className="kpi-overline">Computed Score</span>
            <span className={`font-display font-extrabold text-2xl tabular-nums ${scoreOf(form.probability, form.impact) >= 6 ? 'text-red-600' : scoreOf(form.probability, form.impact) >= 3 ? 'text-amber-600' : 'text-emerald-600'}`}>
              {scoreOf(form.probability, form.impact)}
            </span>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-100 font-display">Cancel</button>
          <button type="submit" disabled={busy} data-testid="risk-form-save"
            className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : 'Add Risk'}
          </button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">{label}</label>
    {children}
  </div>
);
